/**
 * pwa.js — Detección de plataforma para el flujo de instalación.
 *
 * Todo son funciones puras sobre `navigator`/`window` para poder testearlas sin
 * montar componentes. No cachean el resultado en módulo: `isStandalone()` puede
 * cambiar dentro de la misma sesión (el usuario instala y abre la app).
 */

/**
 * ¿La app corre instalada (pantalla de inicio / WebAPK) y no en una pestaña?
 *
 * Android y escritorio exponen el media query `display-mode`; iOS nunca lo
 * implementó y usa el legacy `navigator.standalone`.
 */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia?.("(display-mode: standalone)")?.matches === true) return true;
  } catch {
    // matchMedia puede no existir en entornos de test
  }
  return window.navigator?.standalone === true;
}

/** iOS o iPadOS. */
export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ se reporta como macOS; se delata por los puntos de contacto.
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

/**
 * Android — la única plataforma donde el tono de la alerta se puede cambiar.
 *
 * El sonido no lo decide la web push sino el canal de notificación del sistema,
 * y solo Android expone ese canal al usuario. iOS no tiene equivalente: usa
 * siempre el tono estándar para notificaciones web.
 */
export function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /android/i.test(navigator.userAgent || "");
}

/**
 * Safari en iOS — el único navegador de iOS capaz de añadir a pantalla de
 * inicio. Chrome/Firefox/Edge en iOS son WebKit por dentro pero no exponen la
 * acción, así que mostrarles el instructivo sería mentirles.
 */
export function isIOSSafari() {
  if (!isIOS()) return false;
  const ua = navigator.userAgent || "";
  return !/CriOS|FxiOS|EdgiOS|OPiOS|GSA\//i.test(ua);
}

/**
 * ¿Estamos dentro del contenedor nativo de Capacitor?
 *
 * La app móvil nativa (rama feat/app-movil) usa push nativo vía FCM/APNs: ahí
 * el service worker no aporta y su caché podría servir un bundle viejo dentro
 * del WebView. El acceso es defensivo porque Capacitor no existe en web.
 */
export function isNativeShell() {
  if (typeof window === "undefined") return false;
  return window.Capacitor?.isNativePlatform?.() === true;
}
